import type { QueryClient } from '@tanstack/react-query';
import { calendarResponseSchema, type CalendarResponse } from '@complan/contracts';

/**
 * Calendar freshness rules for the acting user (task 22.7, R13.3 and R13.5).
 *
 * Two distinct guarantees live here, both keyed off the `version` that travels
 * with every {@link import('@complan/contracts').CalendarBar} and every Initiative
 * detail read:
 *
 * - **R13.3 (own-save freshness).** When the acting user saves a change the
 *   calendar renders, the mutation invalidates the calendar query key, so the
 *   bar re-renders from the fresh server read within 3 s and without a manual
 *   reload. That is a cache invalidation, not a diff: the server is the single
 *   source of the rendered values, and re-reading it is what keeps the screen and
 *   the store in agreement (design.md, "Freshness"). The prefix `['calendar']`
 *   matches every filter signature `queryKeys.calendar` produces, so the user's
 *   calendar refreshes whatever filters it currently holds.
 * - **R13.5 (outdated-entry marking).** Opening a Detail_Panel reads the current
 *   record `version`. If that exceeds the version the calendar bar carried, the
 *   bar is showing stale values: mark the entry outdated and re-render it from
 *   current values by invalidating the calendar key. {@link isEntryOutdated} is
 *   the pure comparison; {@link findBarVersion} recovers the version the bar
 *   carried out of the calendar cache without a second server round trip.
 *
 * The comparison and the cache lookup are pure and framework-free so they can be
 * unit-tested without a DOM; only the hooks (`use-calendar-freshness.ts`) touch
 * React Query.
 */

/** The TanStack Query key prefix every calendar query shares (see `queryKeys.calendar`). */
export const CALENDAR_QUERY_KEY_PREFIX = 'calendar';

/** The key prefix the year's Sales_Cycle definition queries share (see `queryKeys.salesCycles`). */
export const SALES_CYCLES_QUERY_KEY_PREFIX = 'sales-cycles';

/**
 * Whether a calendar entry is outdated: the detail read returned a record version
 * strictly greater than the version the calendar bar carried (R13.5).
 *
 * The comparison is deliberately strict. An equal version means the bar and the
 * detail describe the same persisted state, so nothing is stale; only a detail
 * version *later* than the rendered one proves the bar predates a persisted
 * change. A `null` on either side means the comparison cannot be made — the bar
 * was never found in cache, or the detail has not loaded — and an unknown
 * comparison is never treated as outdated, so the indicator never flickers on
 * a race between the two reads.
 */
export function isEntryOutdated(
  barVersion: number | null,
  detailVersion: number | null,
): boolean {
  if (barVersion === null || detailVersion === null) {
    return false;
  }
  return detailVersion > barVersion;
}

/**
 * The version the calendar bar for `initiativeId` carried, recovered from the
 * calendar cache, or `null` when no cached calendar rendering holds that bar.
 *
 * The Detail_Panel opens from a rendered bar, so the bar's version is already in
 * the calendar query cache; reading it there avoids threading the version through
 * the route params (which would go stale the moment the calendar refetched) and
 * avoids a second server call. Every cached calendar query is scanned because the
 * user may have more than one filter signature cached (e.g. after switching
 * filters); the first bar found for the id wins, and its version is what the
 * currently mounted grid rendered.
 *
 * Cached bodies are re-validated through the contracts schema before use, so a
 * cache entry written by an older client shape cannot mis-report a version; a
 * body that does not parse contributes no bar rather than throwing.
 */
export function findBarVersion(client: QueryClient, initiativeId: string): number | null {
  const entries = client.getQueriesData<unknown>({ queryKey: [CALENDAR_QUERY_KEY_PREFIX] });
  for (const [, data] of entries) {
    if (data === undefined) {
      continue;
    }
    const parsed = calendarResponseSchema.safeParse(data);
    if (!parsed.success) {
      continue;
    }
    const version = versionOfBar(parsed.data, initiativeId);
    if (version !== null) {
      return version;
    }
  }
  return null;
}

/** The version of the bar for `initiativeId` in one calendar response, or `null`. */
function versionOfBar(response: CalendarResponse, initiativeId: string): number | null {
  const bar = response.bars.find((candidate) => candidate.id === initiativeId);
  return bar?.version ?? null;
}
